import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import { ReactComponent as CloseModal } from 'assets/images/ic_close_modal.svg';
import { ReactComponent as UploadIcon } from 'assets/images/upload.svg';
import { ReactComponent as TemplateIcon } from 'assets/images/need_template.svg';
import { ReactComponent as DownloadTemplate } from 'assets/images/download_template.svg';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import Dropzone, { FileRejection } from 'react-dropzone';
import { Toast } from 'shared/helpers';
import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { AISystemsService } from 'services/AISystemService';
import { DatasetsService } from 'services';
import { VulnerabilityService } from 'services/VulnerabilityService';
import { downloadFileSaveAs } from 'shared/helpers/DirectDownload';
import { ManageEmployeesService } from 'services/ManageEmployeesService';

interface IBuldUploadProps {
  openBulk: boolean;
  onClose: (reload: boolean) => void;
  title: string;
  variant: 'aisystem' | 'dataset' | 'vulnerabilities' | 'employees';
}

export interface IError {
  row: number;
  errors: string[];
}

export interface IUploadError {
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: IError[];
}

const DialogBulkUpload = (props: IBuldUploadProps): any => {
  const { openBulk, onClose, title, variant } = props;
  const [files, setFiles] = useState<File[]>([]);
  const [showErrorView, setShowErrorView] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [errors, setErros] = useState<IUploadError>({
    totalRows: 0,
    successCount: 0,
    errorCount: 0,
    errors: [],
  });

  // UI Elements
  const actionOnRemoveFile = (index: number): void => {
    files.splice(index, 1);
    setFiles([...files]);
  };

  const actionOnFileSelected = (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[]
  ): void => {
    if (rejectedFiles.length > 0) {
      Toast.showInfoMessage('Please select one valid xlsx, csv file');
      return;
    }
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const MAX_SIZE = 10;
      if (file.size > MAX_SIZE * 1024 * 1024) {
        Toast.showInfoMessage(`File size cannot be exceed ${MAX_SIZE} MB`);
        return;
      }
    }
    setFiles(acceptedFiles);
  };

  const actionOnUpload = (): void => {
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', files[0]);
    let request;

    if (variant === 'aisystem') {
      request = AISystemsService.bulkCreate(formData);
    } else if (variant === 'dataset') {
      request = DatasetsService.bulkCreate(formData);
    } else if (variant === 'employees') {
      request = ManageEmployeesService.bulkUpload(formData);
    } else {
      request = VulnerabilityService.bulkCreate(formData);
    }
    request
      .then((respopnse) => {
        console.log(respopnse);
        Toast.showSuccessMessage('Upload completed successfully');
        onClose(true);
      })
      .catch((error) => {
        if (error.response.data) {
          const { data, msg } = error.response.data;

          if (data && (data.successCount > 0 || data.errorCount > 0)) {
            setShowErrorView(true);
            setErros(data);
          } else if (data && data.successCount === 0 && data.errorCount === 0) {
            Toast.showErrorMessage('Atleast one row is required');
          } else {
            Toast.showErrorMessage(msg);
          }
          setIsUploading(false);
        }
      });
  };

  const actionOnDownloadTemplate = async (): Promise<void> => {
    let fileUrl = '';
    let fileName = '';

    if (variant === 'aisystem') {
      fileUrl = `${window.location.origin}/templates/AI_System_Template.xlsx`;
      fileName = 'AI_System_Template.xlsx';
    } else if (variant === 'dataset') {
      fileUrl = `${window.location.origin}/templates/Dataset_Template.xlsx`;
      fileName = 'Dataset_Template.xlsx';
    } else if (variant === 'employees') {
      fileUrl = `${window.location.origin}/templates/Employee_Template.xlsx`;
      fileName = 'Employee_Template.xlsx';
    } else {
      fileUrl = `${window.location.origin}/templates/Vulnerability_Template.xlsx`;
      fileName = 'Vulnerability_Template.xlsx';
    }

    await downloadFileSaveAs(fileUrl, fileName);
  };

  // UI Elements
  return (
    <Dialog
      open={openBulk}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: {
            xs: 'calc(100% - 24px)',
            sm: '90%',
            md: '75%',
            lg: '65vw',
          },
          maxWidth: '1200px',
          margin: {
            xs: '12px',
            sm: '24px',
          },
        },
      }}
    >
      <DialogContent
        sx={{
          p: {
            xs: 1,
            sm: 2,
            md: 3,
          },
        }}
      >
        <Box
          sx={{
            background: 'white',
            p: {
              xs: 1.5,
              sm: 2,
              md: 3,
            },
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Typography
              variant="h3"
              fontWeight={700}
              sx={{
                fontSize: {
                  xs: '20px',
                  sm: '24px',
                  md: '28px',
                },
                minWidth: 0,
                overflowWrap: 'anywhere',
              }}
            >
              {title}
            </Typography>

            <SvgIcon
              component={CloseModal}
              sx={{
                cursor: 'pointer',
                flexShrink: 0,
                fontSize: {
                  xs: 22,
                  sm: 26,
                },
              }}
              onClick={() => {
                onClose(false);
              }}
            />
          </Stack>

          {!showErrorView && (
            <>
              {false && (
                <>
                  {/* Upload details progress */}
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        color: '#7E7E7E',
                      }}
                    >
                      <Typography variant="body1">Step 1 of 4</Typography>

                      <Typography variant="body1">25% Complete</Typography>
                    </Box>

                    <Box mt={3}>
                      <LinearProgress
                        variant="determinate"
                        value={25}
                        sx={{
                          height: '20px',
                          borderRadius: 5,
                          backgroundColor: '#D1F27A',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#06362B',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </>
              )}

              <Box
                sx={{
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="h3"
                  fontWeight={700}
                  sx={{
                    mt: {
                      xs: 3,
                      sm: 5,
                      md: 8,
                    },
                    fontSize: {
                      xs: '20px',
                      sm: '24px',
                      md: '28px',
                    },
                  }}
                >
                  Upload data file
                </Typography>

                <Typography
                  sx={{
                    color: '#7E7E7E',
                    fontSize: {
                      xs: '14px',
                      sm: '16px',
                    },
                    mt: 1,
                  }}
                >
                  Upload an Excel (.xlsx) or CSV file containing your data
                  information.
                </Typography>

                <Dropzone
                  disabled={files.length > 0}
                  accept={{
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                      ['.xlsx'],
                    'text/csv': ['.csv'],
                  }}
                  maxFiles={1}
                  onDrop={actionOnFileSelected}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />

                      <Box
                        sx={{
                          cursor: 'pointer',
                          border: '1px dashed lightgray',
                          p: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                          },
                          mt: {
                            xs: 3,
                            sm: 5,
                            md: 8,
                          },
                          minHeight: {
                            xs: '180px',
                            sm: '220px',
                            md: '250px',
                          },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxSizing: 'border-box',
                          width: '100%',
                        }}
                      >
                        {files.map((file, index) => (
                          <Stack
                            key={file.name}
                            direction={{
                              xs: 'column',
                              sm: 'row',
                            }}
                            justifyContent="center"
                            spacing={{
                              xs: 1,
                              sm: 3,
                            }}
                            alignItems="center"
                            sx={{
                              width: '100%',
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              variant="h3"
                              sx={{
                                fontSize: {
                                  xs: '16px',
                                  sm: '20px',
                                  md: '24px',
                                },
                                maxWidth: '100%',
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                textAlign: 'center',
                              }}
                            >
                              {file.name}
                            </Typography>

                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                actionOnRemoveFile(index);
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Stack>
                        ))}

                        {files.length === 0 && (
                          <>
                            <UploadIcon />

                            <Typography
                              variant="h3"
                              fontWeight={700}
                              sx={{
                                mt: {
                                  xs: 3,
                                  sm: 5,
                                  md: 8,
                                },
                                fontSize: {
                                  xs: '17px',
                                  sm: '21px',
                                  md: '24px',
                                },
                              }}
                            >
                              Drag and drop your file here
                            </Typography>

                            <Typography
                              sx={{
                                color: '#7E7E7E',
                                fontSize: {
                                  xs: '13px',
                                  sm: '15px',
                                },
                                lineHeight: 1.6,
                              }}
                            >
                              or click to browse and select a file
                              <br />
                              Supports Excel (.xlsx) and CSV files up to 10MB
                            </Typography>
                          </>
                        )}
                      </Box>
                    </div>
                  )}
                </Dropzone>
              </Box>

              <Stack
                sx={{
                  mt: {
                    xs: 2,
                    sm: 3,
                    md: 5,
                  },
                }}
                justifyContent="flex-end"
                direction="row"
              >
                <AppPrimaryButton
                  className="btn-small"
                  title="Upload"
                  disabled={files.length === 0 || isUploading}
                  onClick={actionOnUpload}
                  isLoading={isUploading}
                />
              </Stack>

              <Box
                sx={{
                  mt: {
                    xs: 4,
                    sm: 6,
                    md: 8,
                  },
                }}
              >
                <Typography
                  variant="h3"
                  fontWeight={700}
                  sx={{
                    fontSize: {
                      xs: '18px',
                      sm: '22px',
                      md: '26px',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <TemplateIcon />

                    <span
                      style={{
                        position: 'relative',
                        top: '-2px',
                      }}
                    >
                      Need a template?
                    </span>
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    color: '#7E7E7E',
                    fontSize: {
                      xs: '14px',
                      sm: '16px',
                    },
                    mt: 1,
                  }}
                >
                  Download our template to ensure your data is formatted
                  correctly for import.
                </Typography>
              </Box>

              <Box
                sx={{
                  mt: {
                    xs: 3,
                    sm: 5,
                    md: 8,
                  },
                }}
              >
                <AppPrimaryButton
                  title="Download template"
                  className="btn-small"
                  onClick={actionOnDownloadTemplate}
                  startIcon={
                    <SvgIcon
                      component={DownloadTemplate}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="#76A81F"
                    />
                  }
                />
              </Box>

              <Box
                sx={{
                  mt: {
                    xs: 3,
                    sm: 5,
                    md: 8,
                  },
                }}
              >
                <Typography
                  sx={{
                    color: '#7E7E7E',
                    fontSize: {
                      xs: '13px',
                      sm: '15px',
                    },
                  }}
                >
                  • File must be in Excel (.xlsx) or CSV format
                </Typography>

                <Typography
                  sx={{
                    color: '#7E7E7E',
                    fontSize: {
                      xs: '13px',
                      sm: '15px',
                    },
                  }}
                >
                  • First row should contain column headers
                </Typography>

                <Typography
                  sx={{
                    color: '#7E7E7E',
                    fontSize: {
                      xs: '13px',
                      sm: '15px',
                    },
                  }}
                >
                  • Maximum file size: 10MB
                </Typography>

                <Typography
                  sx={{
                    color: '#7E7E7E',
                    fontSize: {
                      xs: '13px',
                      sm: '15px',
                    },
                  }}
                >
                  • Ensure all required fields are included
                </Typography>

                <Typography
                  sx={{
                    color: '#7E7E7E',
                    fontSize: {
                      xs: '13px',
                      sm: '15px',
                    },
                  }}
                >
                  • Use the template for best results
                </Typography>
              </Box>
            </>
          )}

          {/* Validation Error View */}
          {showErrorView && (
            <>
              <Stack
                alignItems="center"
                spacing={2}
                sx={{
                  textAlign: 'center',
                  mt: {
                    xs: 3,
                    sm: 4,
                  },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: {
                      xs: '20px',
                      sm: '24px',
                    },
                  }}
                >
                  Validation Failed
                </Typography>

                <Typography
                  className="text-gray"
                  sx={{
                    fontSize: {
                      xs: '14px',
                      sm: '16px',
                    },
                  }}
                >
                  Please review the validaton errors below
                </Typography>
              </Stack>

              {/* Summary */}
              <Box
                className="app-box-border"
                sx={{
                  mt: {
                    xs: 3,
                    sm: 5,
                  },
                  p: {
                    xs: 2,
                    sm: 3,
                    md: 4,
                  },
                }}
              >
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Stack
                      direction={{
                        xs: 'column',
                        sm: 'row',
                      }}
                      justifyContent="space-between"
                      alignItems={{
                        xs: 'flex-start',
                        sm: 'center',
                      }}
                      spacing={0.5}
                    >
                      <Typography className="text-gray">File Name</Typography>

                      <Typography
                        className="fw-6"
                        sx={{
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                          textAlign: {
                            xs: 'left',
                            sm: 'right',
                          },
                        }}
                      >
                        {files[0].name}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography className="text-gray">Valid Rows</Typography>

                      <Typography className="fw-6" sx={{ color: 'green' }}>
                        {errors.successCount}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography className="text-gray">Total Rows</Typography>

                      <Typography className="fw-6">
                        {errors.totalRows}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography className="text-gray">
                        Invalid Rows
                      </Typography>

                      <Typography className="fw-6" sx={{ color: 'red' }}>
                        {errors.errorCount}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              <Stack
                sx={{
                  mt: {
                    xs: 3,
                    sm: 5,
                  },
                }}
                justifyContent="flex-end"
                direction="row"
              >
                <AppPrimaryButton
                  className="btn-small"
                  title="Fix"
                  onClick={() => {
                    setShowErrorView(false);
                    setFiles([]);
                  }}
                />
              </Stack>

              {/* Validation errors */}
              <Box
                className="app-box-border"
                sx={{
                  mt: {
                    xs: 3,
                    sm: 5,
                  },
                  p: {
                    xs: 2,
                    sm: 3,
                    md: 4,
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={{
                    xs: 1.5,
                    sm: 4,
                  }}
                  alignItems="flex-start"
                >
                  <InfoIcon
                    color="error"
                    sx={{
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  />

                  <Stack minWidth={0}>
                    <Typography className="fw-6">
                      Validation Errors Found
                    </Typography>

                    <Typography
                      className="text-gray"
                      sx={{
                        fontSize: {
                          xs: '14px',
                          sm: '16px',
                        },
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {errors.errorCount} row(s) failed validation. Please fix
                      the errors below and try again
                    </Typography>
                  </Stack>
                </Stack>

                {errors.errors.map((error) => (
                  <Stack
                    key={`error_row_${error.row}`}
                    className="app-box-border"
                    sx={{
                      p: {
                        xs: 1.5,
                        sm: 2,
                      },
                      mt: {
                        xs: 2,
                        sm: 3,
                      },
                    }}
                  >
                    <Typography>Row {error.row}:</Typography>

                    {error.errors.map((e) => (
                      <Typography
                        key={e}
                        className="text-gray"
                        sx={{
                          fontSize: {
                            xs: '13px',
                            sm: '15px',
                          },
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                        }}
                      >
                        {e}
                      </Typography>
                    ))}
                  </Stack>
                ))}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DialogBulkUpload;
